import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Row, Col, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import ImageUploadBox from "../../components/ImageUploadBox";

export default function AddFocus() {
  const baseURL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("profileToken");
  const { id } = useParams(); // ✅ edit mode
  const navigate = useNavigate();

  const ICONS = [
    { label: "Store", value: "fa-store" },
    { label: "Utensils", value: "fa-utensils" },
    { label: "Truck", value: "fa-truck" },
    { label: "Shopping Cart", value: "fa-shopping-cart" },
    { label: "Warehouse", value: "fa-warehouse" },
    { label: "Box", value: "fa-box" },
    { label: "Hand Holding", value: "fa-hand-holding" },
    { label: "Handshake", value: "fa-handshake" },
    { label: "Chart", value: "fa-chart-line" },
    { label: "Globe", value: "fa-globe" },
    { label: "Users", value: "fa-users" },
    { label: "Building", value: "fa-building" },
  ];

  const initialState = { text: "", icon: "" };

  const [formData, setFormData] = useState(initialState);
  const [imageFile, setImageFile] = useState(null); // ✅ string | File | null
  const [loading, setLoading] = useState(false);

  const selectedIcon = ICONS.find((x) => x.value === formData.icon);

  // ✅ Fetch focus details for edit
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/focus/${id}`, {
          headers: { "x-access-token": token || "" },
        });

        if (data?.status && data?.result) {
          const f = data.result;

          setFormData({
            text: f.text || "",
            icon: f.icon || "",
          });

          setImageFile(f.image || null); // ✅ old image filename as string
        } else {
          Swal.fire("Error", data?.message || "Focus area not found", "error");
          navigate("/focus-list");
        }
      } catch (err) {
        console.error("❌ Error fetching focus:", err);
        Swal.fire("Error", "Failed to fetch focus details", "error");
        navigate("/focus-list");
      }
    })();
  }, [id, baseURL, token, navigate]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageChange = (e) => {
    setImageFile(e.target.files?.[0] || null); // File
  };

  const handleReset = () => {
    setFormData(initialState);
    setImageFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.text.trim()) {
      Swal.fire("Error", "Focus Area Name is required", "error");
      return;
    }
    if (!formData.icon) {
      Swal.fire("Error", "Icon is required", "error");
      return;
    }

    // ✅ Add: image required | Update: image optional
    if (!id && !imageFile) {
      Swal.fire("Error", "Image is required", "error");
      return;
    }

    if (!token) {
      Swal.fire("Error", "Unauthorized. Please login again.", "error");
      return;
    }

    try {
      setLoading(true);

      const data = new FormData();
      data.append("text", formData.text.trim());
      data.append("icon", formData.icon);

      // ✅ only append if new File selected
      if (imageFile && typeof imageFile !== "string") {
        data.append("image", imageFile);
      }

      const headers = {
        "Content-Type": "multipart/form-data",
        "x-access-token": token || "",
      };

      if (id) {
        // ✅ update
        const res = await axios.put(`${baseURL}/focus/update/${id}`, data, { headers });

        if (res.data?.status) {
          Swal.fire("Success", res.data.message || "✅ Focus Area updated!", "success");
          navigate("/focus-list");
        } else {
          Swal.fire("Error", res.data?.message || "Failed to update focus area", "error");
        }
      } else {
        // ✅ add
        const res = await axios.post(`${baseURL}/focus/add`, data, { headers });

        if (res.data?.status) {
          Swal.fire("Success", res.data.message || "✅ Focus Area added!", "success");
          handleReset();
          navigate("/focus-list");
        } else {
          Swal.fire("Error", res.data?.message || "Failed to add focus area", "error");
        }
      }
    } catch (err) {
      console.error("❌ Error saving focus:", err);
      Swal.fire("Error", err.response?.data?.message || "Server error", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      <h3 className="fw-bold text-center mb-4 text-success">
        {id ? "Update Focus Area" : "Add New Focus Area"}
      </h3>

      <form onSubmit={handleSubmit}>
        <div className="border border-success rounded-4 p-4">
          <Row className="g-4">
            <Col xs={12} md={6}>
              <ImageUploadBox
                id="focusImageInput"
                label="Focus image"
                value={imageFile}
                onChange={handleImageChange}
                baseURL={baseURL}
                required={!id}          // ✅ add me required, update me optional
                isEdit={!!id}
                variant="dashed"
                height={150}
              />
            </Col>

            <Col xs={12} md={6}>
              <div className="mb-3">
                <label htmlFor="icon" className="form-label">
                  Select Icon <span className="text-danger">*</span>
                </label>

                <Row className="g-3">
                  <Col xs={12} md={4}>
                    <div
                      className="d-flex flex-column align-items-center justify-content-center border rounded-3 px-3"
                      style={{
                        width: 140,
                        height: 140,
                        minWidth: 140,
                        background: formData.icon ? "#f8fff9" : "#f8f9fa",
                        borderColor: formData.icon
                          ? "rgba(25,135,84,0.35)"
                          : "rgba(0,0,0,0.12)",
                        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                      }}
                      title="Icon Preview"
                    >
                      <div
                        className="d-flex align-items-center justify-content-center rounded-3"
                        style={{
                          width: 130,
                          height: 130,
                          background: "#ffffff",
                          border: "1px solid rgba(0,0,0,0.08)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                        }}
                      >
                        {formData.icon ? (
                          <i className={`fas ${formData.icon}`} style={{ fontSize: 70 }} />
                        ) : (
                          <span style={{ fontSize: 18, opacity: 0.7 }}>Preview</span>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col xs={12} md={8} className="align-content-center">
                    <select
                      id="icon"
                      className="form-select"
                      value={formData.icon}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Icon</option>
                      {ICONS.map((i) => (
                        <option key={i.value} value={i.value}>
                          {i.label}
                        </option>
                      ))}
                    </select>

                    <small className="text-muted" style={{ fontSize: 14 }}>
                      {selectedIcon?.label ? `Selected: ${selectedIcon.label}` : "Pick an icon from the list."}
                    </small>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <Row className="g-4 mt-1">
            <Col xs={12} md={6}>
              <div>
                <label htmlFor="text" className="form-label">
                  Focus Area Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="text"
                  className="form-control"
                  placeholder="e.g. MODERN TRADE"
                  value={formData.text}
                  onChange={handleChange}
                  required
                />
              </div>
            </Col>

            <Col xs={12} md={6}>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="outline-dark"
                  className="rounded-pill px-4"
                  onClick={handleReset}
                  type="button"
                >
                  Reset
                </Button>

                <Button
                  variant="success"
                  className="rounded-pill px-4"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : id ? "Update" : "Save"}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </form>
    </div>
  );
}
